import { useState, useEffect } from 'react';
import { devicesApi, Device } from '../../api/devices.api';
import { branchesApi, Branch } from '../../api/branches.api';
import { companiesApi, Company } from '../../api/companies.api';
import Button from '../../components/forms/Button';
import Select from '../../components/forms/Select';
import Loading from '../../components/common/Loading';
import Table, { Column } from '../../components/common/Table';
import { generateComputerReportPDF } from '../../utils/pdfGenerator';

interface ComputerSpec {
  processor: string;
  disk: string;
  ram: string;
}

interface ComputerDevice extends Device {
  specs: ComputerSpec;
}

const ComputerReportPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [devices, setDevices] = useState<ComputerDevice[]>([]);
  const [filteredDevices, setFilteredDevices] = useState<ComputerDevice[]>([]);

  const [branches, setBranches] = useState<Branch[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);

  // Filter options extracted from device names
  const [processors, setProcessors] = useState<string[]>([]);
  const [diskSizes, setDiskSizes] = useState<string[]>([]);
  const [ramSizes, setRamSizes] = useState<string[]>([]);

  // Filter values
  const [branchId, setBranchId] = useState<string>('all');
  const [companyId, setCompanyId] = useState<string>('all');
  const [processor, setProcessor] = useState<string>('all');
  const [diskSize, setDiskSize] = useState<string>('all');
  const [ramSize, setRamSize] = useState<string>('all');
  const [status, setStatus] = useState<string>('all');

  useEffect(() => {
    fetchFilterOptions();
    fetchDevices();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [devices, branchId, companyId, processor, diskSize, ramSize, status]);

  const fetchFilterOptions = async () => {
    try {
      const [branchesData, companiesData] = await Promise.all([
        branchesApi.getAll(),
        companiesApi.getAll(),
      ]);
      setBranches(branchesData.filter(b => b.isActive).sort((a, b) => a.name.localeCompare(b.name, 'tr')));
      setCompanies(companiesData.filter(c => c.isActive).sort((a, b) => a.name.localeCompare(b.name, 'tr')));
    } catch (err: any) {
      console.error('Filtre seçenekleri yüklenirken hata:', err);
    }
  };

  const fetchDevices = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await devicesApi.getComputerReport();
      
      const computersWithSpecs: ComputerDevice[] = response.devices.map((device: any) => ({
        ...device,
        specs: {
          processor: device.processor,
          disk: device.disk,
          ram: device.ram,
        },
      }));

      setDevices(computersWithSpecs);
      
      setProcessors(response.filters.processors);
      setDiskSizes(response.filters.diskSizes);
      setRamSizes(response.filters.ramSizes);
    } catch (err: any) {
      setError(err.message || 'Cihazlar yüklenirken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...devices];

    if (branchId !== 'all') {
      filtered = filtered.filter(device => {
        const deviceBranchId = typeof device.branch === 'object' 
          ? (device.branch as any)._id || (device.branch as any).id 
          : device.branch;
        return deviceBranchId === branchId;
      });
    }

    if (companyId !== 'all') {
      filtered = filtered.filter(device => {
        const branch = branches.find(b => {
          const deviceBranchId = typeof device.branch === 'object' 
            ? (device.branch as any)._id || (device.branch as any).id 
            : device.branch;
          return b.id === deviceBranchId;
        });
        const branchCompanyId = typeof branch?.company === 'object'
          ? (branch.company as any)._id || (branch.company as any).id
          : branch?.company;
        return branchCompanyId === companyId;
      });
    }

    if (processor !== 'all') {
      filtered = filtered.filter(device => device.specs.processor === processor);
    }

    if (diskSize !== 'all') {
      filtered = filtered.filter(device => device.specs.disk === diskSize);
    }

    if (ramSize !== 'all') {
      filtered = filtered.filter(device => device.specs.ram === ramSize);
    }

    if (status !== 'all') {
      filtered = filtered.filter(device => device.status === status);
    }

    setFilteredDevices(filtered);
  };

  const handleClearFilters = () => {
    setBranchId('all');
    setCompanyId('all');
    setProcessor('all');
    setDiskSize('all');
    setRamSize('all');
    setStatus('all');
  };

  const handleExportPDF = () => {
    if (filteredDevices.length > 0) {
      generateComputerReportPDF(filteredDevices);
    }
  };

  const getBranchName = (device: Device): string => {
    if (device.branch) {
      if (typeof device.branch === 'object') {
        return (device.branch as any).name || '-';
      }
      const branch = branches.find(b => b.id === device.branch);
      return branch?.name || '-';
    }
    return '-';
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, string> = {
      Available: 'bg-green-100 text-green-800',
      Assigned: 'bg-blue-100 text-blue-800',
      InRepair: 'bg-yellow-100 text-yellow-800',
      Retired: 'bg-gray-100 text-gray-800',
      Lost: 'bg-red-100 text-red-800',
      Scrapped: 'bg-red-100 text-red-800',
    };
    const labels: Record<string, string> = {
      Available: 'Şubede',
      Assigned: 'Zimmetli',
      InRepair: 'Onarımda',
      Retired: 'Kullanım Dışı',
      Lost: 'Kayıp',
      Scrapped: 'Hurda',
    };
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${badges[status] || 'bg-gray-100 text-gray-800'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const columns: Column<ComputerDevice>[] = [
    {
      key: 'serialNumber',
      header: 'Seri No',
      render: (item) => {
        if (!item.serialNumber) return '-';
        const isMongoId = /^[a-f\d]{24}$/i.test(item.serialNumber);
        return isMongoId ? (
          <span className="text-xs text-gray-500" title="Otomatik oluşturuldu">
            {item.serialNumber.substring(0, 8)}...
          </span>
        ) : (
          item.serialNumber
        );
      },
    },
    {
      key: 'processor',
      header: 'İşlemci',
      render: (item) => <span className="text-sm">{item.specs.processor}</span>,
    },
    {
      key: 'disk',
      header: 'Disk',
      render: (item) => <span className="text-sm font-medium">{item.specs.disk}</span>,
    },
    {
      key: 'ram',
      header: 'RAM',
      render: (item) => <span className="text-sm font-medium">{item.specs.ram}</span>,
    },
    {
      key: 'branch',
      header: 'Şube',
      render: (item) => <span className="text-sm">{getBranchName(item)}</span>,
    },
    {
      key: 'status',
      header: 'Durum',
      render: (item) => getStatusBadge(item.status),
    },
    {
      key: 'assetTag',
      header: 'Varlık Etiketi',
      render: (item) => item.assetTag || '-',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Bilgisayar Raporu</h1>
          <p className="text-sm text-gray-600 mt-1">
            İşlemci, disk ve RAM özelliklerine göre bilgisayarları filtreleyin
          </p>
        </div>
        {filteredDevices.length > 0 && (
          <Button variant="secondary" onClick={handleExportPDF}>
            📄 PDF Olarak Kaydet
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Filtreler</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          <Select
            label="İşlemci"
            value={processor}
            onChange={(value) => setProcessor(value)}
            options={[
              { value: 'all', label: 'Tüm İşlemciler' },
              ...processors.map(p => ({ value: p, label: p }))
            ]}
            searchable={true}
          />

          <Select
            label="Disk Boyutu"
            value={diskSize}
            onChange={(value) => setDiskSize(value)}
            options={[
              { value: 'all', label: 'Tüm Disk Boyutları' },
              ...diskSizes.map(d => ({ value: d, label: d }))
            ]}
            searchable={true}
          />

          <Select
            label="RAM Boyutu"
            value={ramSize}
            onChange={(value) => setRamSize(value)}
            options={[
              { value: 'all', label: 'Tüm RAM Boyutları' },
              ...ramSizes.map(r => ({ value: r, label: r }))
            ]}
            searchable={true}
          />

          <Select
            label="Şube"
            value={branchId}
            onChange={(value) => setBranchId(value)}
            options={[
              { value: 'all', label: 'Tüm Şubeler' },
              ...branches.map(b => ({ value: b.id, label: b.name }))
            ]}
            searchable={true}
          />

          <Select
            label="Şirket"
            value={companyId}
            onChange={(value) => setCompanyId(value)}
            options={[
              { value: 'all', label: 'Tüm Şirketler' },
              ...companies.map(c => ({ value: c.id, label: c.name }))
            ]}
            searchable={true}
          />

          <Select
            label="Durum"
            value={status}
            onChange={(value) => setStatus(value)}
            options={[
              { value: 'all', label: 'Tüm Durumlar' },
              { value: 'Available', label: 'Şubede' },
              { value: 'Assigned', label: 'Zimmetli' },
              { value: 'InRepair', label: 'Onarımda' },
              { value: 'Retired', label: 'Kullanım Dışı' },
              { value: 'Scrapped', label: 'Hurda' },
            ]}
            searchable={true}
          />
        </div>

        <div className="flex gap-3">
          <Button variant="outline" onClick={handleClearFilters}>
            Filtreleri Temizle
          </Button>
        </div>
      </div>

      {/* Summary */}
      {!isLoading && filteredDevices.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-sm font-medium text-blue-900">
              Toplam <span className="font-bold">{filteredDevices.length}</span> bilgisayar bulundu
            </p>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
          {error}
        </div>
      )}

      {/* Loading */}
      {isLoading && <Loading text="Bilgisayarlar yükleniyor..." />}

      {/* Report Table */}
      {!isLoading && filteredDevices.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <Table
            data={filteredDevices}
            columns={columns}
            emptyMessage="Filtre kriterlerine uygun bilgisayar bulunamadı"
          />
        </div>
      )}

      {/* No Data */}
      {!isLoading && devices.length === 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <p className="text-gray-500 text-lg font-medium mb-2">Henüz bilgisayar kaydı bulunmuyor</p>
          <p className="text-gray-400 text-sm mb-4">
            Bilgisayar formatında ürün eklendiğinde burada görünecektir
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-2xl mx-auto text-left">
            <p className="text-sm font-semibold text-blue-900 mb-2">📝 Ürün Adı Formatı:</p>
            <p className="text-sm text-blue-800 mb-2">
              Bilgisayarlar için ürün adını şu formatta girin:
            </p>
            <code className="block bg-white px-3 py-2 rounded text-sm text-gray-800 border border-blue-300">
              İşlemci / Disk / RAM
            </code>
            <p className="text-xs text-blue-700 mt-2">
              Örnek: <span className="font-mono">Intel i5 / 256GB SSD / 8GB RAM</span>
            </p>
          </div>
        </div>
      )}

      {!isLoading && devices.length > 0 && filteredDevices.length === 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <p className="text-gray-500 text-lg font-medium mb-2">Filtre kriterlerine uygun sonuç bulunamadı</p>
          <p className="text-gray-400 text-sm">Lütfen filtreleri değiştirerek tekrar deneyin</p>
        </div>
      )}
    </div>
  );
};

export default ComputerReportPage;
